import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Country } from '../models/common/country.model';

type GccCountryKey = 'SA' | 'AE' | 'BH' | 'KW' | 'OM' | 'QA';

export function gccCrNumberValidator(countryResolver: () => Country | null | undefined): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = normalizeDigits(control.value);
        if (!value) return null;

        const countryKey = gccCountryKey(countryResolver());
        if (!countryKey || isValidCr(countryKey, value)) return null;

        return { gccCrNumber: crMessage(countryKey) };
    };
}

export function gccVatNumberValidator(countryResolver: () => Country | null | undefined): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = normalizeDigits(control.value);
        if (!value) return null;

        const countryKey = gccCountryKey(countryResolver());
        if (!countryKey || isValidVat(countryKey, value)) return null;

        return { gccVatNumber: vatMessage(countryKey) };
    };
}

export function gccRegistrationError(control: AbstractControl | null | undefined): string | null {
    const errors = control?.errors;
    return (errors?.['gccCrNumber'] || errors?.['gccVatNumber'] || null) as string | null;
}

function gccCountryKey(country?: Country | null): GccCountryKey | null {
    const code = (country?.countryCode || '').trim().toUpperCase();
    const name = (country?.name || '').trim().toUpperCase();

    if (code === 'SA' || code === 'SAU' || name.includes('SAUDI')) return 'SA';
    if (code === 'AE' || code === 'ARE' || name.includes('UNITED ARAB') || name.includes('UAE')) return 'AE';
    if (code === 'BH' || code === 'BHR' || name.includes('BAHRAIN')) return 'BH';
    if (code === 'KW' || code === 'KWT' || name.includes('KUWAIT')) return 'KW';
    if (code === 'OM' || code === 'OMN' || name.includes('OMAN')) return 'OM';
    if (code === 'QA' || code === 'QAT' || name.includes('QATAR')) return 'QA';
    return null;
}

function isValidCr(countryKey: GccCountryKey, value: string): boolean {
    switch (countryKey) {
        case 'SA':
            return /^\d{10}$/.test(value);
        case 'AE':
            return /^\d{5,15}$/.test(value);
        case 'BH':
        case 'KW':
        case 'OM':
        case 'QA':
            return /^\d{1,20}$/.test(value);
    }
}

function isValidVat(countryKey: GccCountryKey, value: string): boolean {
    switch (countryKey) {
        case 'SA':
            return /^3\d{13}3$/.test(value);
        case 'AE':
            return /^100\d{12}$/.test(value);
        case 'BH':
        case 'OM':
            return /^\d{15}$/.test(value);
        case 'KW':
        case 'QA':
            return /^\d{8,20}$/.test(value);
    }
}

function crMessage(countryKey: GccCountryKey): string {
    switch (countryKey) {
        case 'SA':
            return 'Saudi CR number must be 10 digits.';
        case 'AE':
            return 'UAE trade license / CR number must be 5 to 15 digits.';
        default:
            return 'CR number format is invalid for the selected GCC country.';
    }
}

function vatMessage(countryKey: GccCountryKey): string {
    switch (countryKey) {
        case 'SA':
            return 'Saudi VAT number must be 15 digits and start and end with 3.';
        case 'AE':
            return 'UAE VAT / TRN must be 15 digits and start with 100.';
        case 'BH':
        case 'OM':
            return 'VAT number must be 15 digits for the selected GCC country.';
        default:
            return 'VAT number format is invalid for the selected GCC country.';
    }
}

function normalizeDigits(value: unknown): string {
    return String(value ?? '').replace(/\D/g, '');
}
